import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkle, AlertTriangle, Sun, Droplets, MessageCircle } from "lucide-react";
import { BrandLockup } from "@/components/Brand";

/**
 * Public Tattoo Aftercare page.
 * Same brand backdrop / typography as the rest of the studio portal — adapted from
 * Brian's printed aftercare card.
 */
export default function Aftercare() {
  return (
    <div className="min-h-screen">
      {/* Header — matches Landing nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/30 border-b border-[#D2A853]/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="aftercare-brand">
            <BrandLockup size="sm" />
          </Link>
          <Link to="/" className="overline text-[#A3A8A4] hover:text-[#D2A853] inline-flex items-center gap-2" data-testid="aftercare-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        {/* Title */}
        <div className="text-center stagger-child" style={{ animationDelay: "100ms" }}>
          <span className="overline text-[#D2A853]">Caring for your piece</span>
          <h1 className="font-display italic text-5xl sm:text-6xl lg:text-7xl text-[#E8E9E3] mt-4 tracking-tight" style={{ fontFamily: "Georgia, serif" }}>
            Tattoo Aftercare
          </h1>
          <div className="ornate-rule mt-8 max-w-xs mx-auto">
            <Sparkle className="w-3 h-3" strokeWidth={1.4} />
          </div>
        </div>

        {/* Supplies callout */}
        <div className="mt-12 frame-card p-8 text-center stagger-child" style={{ animationDelay: "200ms" }} data-testid="aftercare-supplies">
          <span className="overline text-[#D2A853] text-[10px]">What to buy</span>
          <p className="font-display text-2xl sm:text-3xl text-[#E8E9E3] mt-3 leading-snug tracking-wide">
            Dial Orange <span className="italic" style={{ fontFamily: "Georgia, serif" }}>Fragrance-Free</span> Antibacterial Soap
            <span className="block text-[#D2A853] mt-1">&amp; Aquaphor</span>
          </p>
        </div>

        {/* Daily routine */}
        <Step n="01" title="Wash &amp; Apply" delay={300}>
          Wash with the soap, let air dry, then apply a small amount of <span className="text-[#D2A853]">Aquaphor</span>. Rub it in thoroughly.
        </Step>

        <div className="mt-10 text-center stagger-child" style={{ animationDelay: "350ms" }}>
          <span className="overline text-[#D2A853] text-[10px]">Frequency</span>
          <p className="font-display italic text-3xl sm:text-4xl text-[#D2A853] mt-3" style={{ fontFamily: "Georgia, serif" }}>
            Repeat 3× a day
          </p>
          <p className="text-sm text-[#A3A8A4] mt-2 tracking-wider uppercase">Breakfast · Lunch · Dinner</p>
        </div>

        <Step n="02" title="Switch to Lotion After Day 3" delay={400}>
          Follow this for the first <span className="text-[#D2A853]">2&ndash;3 days</span>, then switch to lotion <span className="text-[#D2A853]">2× a day</span> for the next week.
        </Step>

        {/* Warning */}
        <div className="mt-10 border border-[#D2A853]/30 bg-black/20 backdrop-blur-sm p-8 stagger-child" style={{ animationDelay: "500ms" }} data-testid="aftercare-warning">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-5 h-5 text-[#D2A853] shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <p className="text-[#E8E9E3] leading-relaxed">
                Your tattoo will start to peel like a sunburn and may scab.
              </p>
              <p className="font-display text-2xl text-[#D2A853] mt-3 tracking-wide">
                Do not pick or scratch at the tattoo.
              </p>
            </div>
          </div>
        </div>

        {/* Lifestyle rules */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10 stagger-child" style={{ animationDelay: "600ms" }}>
          <Rule icon={Sun}     title="Avoid Sun Exposure" body="Keep the fresh tattoo out of direct sun while it heals." />
          <Rule icon={Droplets} title="No Swimming"        body="No swimming or long soaks for 2 weeks. Bathing is okay." />
        </div>

        {/* Reach out */}
        <div className="mt-12 text-center stagger-child" style={{ animationDelay: "700ms" }} data-testid="aftercare-reach-out">
          <MessageCircle className="w-6 h-6 text-[#D2A853] mx-auto mb-4" strokeWidth={1.4} />
          <p className="text-[#C8CFC9] leading-relaxed max-w-xl mx-auto">
            Feel free to reach out if you have any questions. There is no such thing as a dumb question
            when it comes to your tattoo &amp; its aftercare.
          </p>
        </div>

        {/* Sign-off */}
        <div className="mt-16 text-center stagger-child" style={{ animationDelay: "800ms" }}>
          <div className="ornate-rule mb-8 max-w-xs mx-auto">
            <Sparkle className="w-3 h-3" strokeWidth={1.4} />
          </div>
          <p className="font-display italic text-2xl sm:text-3xl text-[#E8E9E3]" style={{ fontFamily: "Georgia, serif" }}>
            Thank you for getting tattooed.
          </p>
          <p className="font-display italic text-3xl sm:text-4xl text-[#D2A853] mt-4" style={{ fontFamily: "Georgia, serif" }}>
            — Brian Billmaier
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#D2A853]/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-12 flex flex-col items-center text-center gap-4">
          <BrandLockup size="sm" />
          <p className="overline text-[#A3A8A4] text-[10px]">Black &amp; Grey Realism · Private Studio</p>
          <Link to="/" className="overline text-[#D2A853] hover:text-[#E2BC6A] inline-flex items-center gap-2 mt-2" data-testid="aftercare-footer-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Step({ n, title, children, delay = 0 }) {
  return (
    <div className="mt-10 grid grid-cols-[auto_1fr] gap-6 items-start stagger-child" style={{ animationDelay: `${delay}ms` }} data-testid={`aftercare-step-${n}`}>
      <div className="font-display text-5xl text-[#D2A853]/40 leading-none" style={{ fontFamily: "Georgia, serif" }}>{n}</div>
      <div>
        <h3 className="font-display text-xl text-[#E8E9E3] tracking-wide mb-2">{title}</h3>
        <p className="text-[#C8CFC9] leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function Rule({ icon: Icon, title, body }) {
  return (
    <div className="frame-card p-6 text-center">
      <Icon className="w-5 h-5 text-[#D2A853] mx-auto mb-3" strokeWidth={1.5} />
      <h4 className="font-display text-base text-[#E8E9E3] tracking-wide mb-2">{title}</h4>
      <p className="text-xs text-[#A3A8A4] leading-relaxed">{body}</p>
    </div>
  );
}
